/**
 * Wondeon Hub dil baglami.
 *
 * Route'lar dile gore catallanmaz: ayni bilesen ve ayni URL, TR/EN metni
 * cizer. Boylece responsive davranis ile erisim kurallari iki ayri uygulamaya
 * donusmez. Giris oncesinde tarayici, giris sonrasinda hesap tercihi kazanir.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LanguagePreference } from '@shared/index';
import { api, setRequestLanguage } from './api';
import { useAuth } from './auth';
import { canonicalCatalogName, setFormatLanguage } from './format';

const STORAGE_KEY = 'wondeon.language';

const tr = {
  'language.switchToEnglish': 'Switch to English',
  'language.switchToTurkish': "Türkçe'ye geç",
  'common.backToWondeon': "wondeon.com’a dön",
  'common.skipToContent': 'İçeriğe atla',
  'common.searchCatalog': 'Katalogda ara',
  'common.signOut': 'Çıkış',
  'common.accountSettings': 'Hesap ayarları',
  'common.theme': 'Tema',
  'common.changeTheme': 'Değiştirmek için tıkla.',
  'common.themeDark': 'koyu',
  'common.themeLight': 'açık',
  'common.themeSystem': 'sistem',
  'common.legalLinks': 'Yasal bağlantılar',
  'common.privacy': 'KVKK',
  'common.terms': 'Kullanım Koşulları',
  'common.open': 'Aç',
  'common.close': 'Kapat',
  'common.save': 'Kaydet',
  'common.cancel': 'Vazgeç',
  'common.loading': 'Yükleniyor…',
  'auth.email': 'E-posta',
  'auth.password': 'Parola',
  'auth.passwordAgain': 'Parola tekrarı',
  'auth.newPassword': 'Yeni parola',
  'auth.newPasswordAgain': 'Yeni parola (tekrar)',
  'auth.fullName': 'Ad soyad',
  'auth.organizationName': 'Kurum adı',
  'auth.signIn': 'Giriş yap',
  'auth.signInSubtitle': 'Kurum hesabınızla Wondeon Hub’a devam edin.',
  'auth.rememberMe': 'Beni hatırla',
  'auth.forgotPassword': 'Parolamı unuttum',
  'auth.or': 'veya',
  'auth.googleSignIn': 'Google ile giriş yap',
  'auth.noAccount': 'Hesabın yok mu?',
  'auth.register': 'Kayıt ol',
  'auth.resendVerification': 'Yeni kod gönder',
  'auth.serverUnavailable': 'Sunucuya ulaşılamadı. İnternet bağlantını kontrol et.',
  'auth.resendSuccess': 'Yeni doğrulama kodu gönderildi. Gelen kutunu kontrol et.',
  'auth.resendFail': 'Gönderilemedi. Biraz sonra tekrar dene.',
  'auth.createOrg': 'Kurum hesabı oluştur',
  'auth.createOrgSubtitle': 'Wondeon Hub’ı kurumunuz adına kullanmaya başlayın.',
  'auth.createAccount': 'Hesap oluştur',
  'auth.haveAccount': 'Zaten hesabınız var mı?',
  'auth.passwordMin': 'En az 10 karakter.',
  'auth.passwordMismatch': 'Parolalar eşleşmiyor.',
  'auth.completeMarked': 'Devam etmek için işaretli alanları tamamla.',
  'auth.confirmPrivacyRead': 'KVKK Aydınlatma Metni’ni okuduğunu teyit et.',
  'auth.confirmTerms': 'Kullanım Koşulları’nı kabul et.',
  'auth.privacyRead': 'KVKK Aydınlatma Metni’ni okudum.',
  'auth.termsAccepted': 'Kullanım Koşulları’nı kabul ediyorum.',
  'auth.checkEmail': 'E-postanı kontrol et',
  'auth.verificationSent': 'adresine altı haneli doğrulama kodu gönderdik.',
  'auth.checkSpam': 'Bağlantı gelmediyse spam klasörüne de bak.',
  'auth.backToSignIn': 'Giriş ekranına dön',
  'auth.strengthTooShort': 'Çok kısa',
  'auth.strengthWeak': 'Zayıf',
  'auth.strengthGood': 'İyi',
  'auth.strengthStrong': 'Güçlü',
  'auth.resetLinkSent': 'Bağlantı gönderildi',
  'auth.resetLinkSentIfRegistered': 'adresi kayıtlıysa, parola sıfırlama bağlantısı gönderildi.',
  'auth.resetLinkValidity': 'Bağlantı 1 saat geçerli. Gelmediyse spam klasörüne bak.',
  'auth.forgotSubtitle': 'E-posta adresini gir, sıfırlama bağlantısı gönderelim.',
  'auth.sendLink': 'Bağlantı gönder',
  'auth.invalidLink': 'Bağlantı geçersiz',
  'auth.invalidResetLink': 'Bu bağlantıda sıfırlama kodu yok. E-postadaki bağlantıya tekrar tıkla veya yeni bir sıfırlama isteği oluştur.',
  'auth.requestNewLink': 'Yeni bağlantı iste',
  'auth.setNewPassword': 'Yeni parola belirle',
  'auth.resetClosesSessions': 'Kaydettikten sonra tüm oturumların kapatılacak.',
  'auth.updatePassword': 'Parolayı güncelle',
  'auth.passwordUpdated': 'Parolan güncellendi. Yeni parolanla giriş yapabilirsin.',
  'auth.verifyingEmail': 'E-posta doğrulanıyor',
  'auth.verifying': 'Doğrulanıyor',
  'auth.emailVerified': 'E-postan doğrulandı',
  'auth.canSignIn': 'Artık giriş yapabilirsin.',
  'auth.verificationFailed': 'Doğrulanamadı',
  'auth.noVerificationCode': 'Bu bağlantıda doğrulama kodu yok.',
  'auth.verificationRetry': 'Bağlantının süresi dolmuş olabilir. Giriş ekranından yeni bir doğrulama bağlantısı isteyebilirsin.',
  'auth.googleVerifying': 'Google hesabı doğrulanıyor',
  'auth.googlePendingMissing': 'Bekleyen bir Google girişi bulunamadı.',
  'auth.googleInfoFailed': 'Google giriş bilgisi alınamadı.',
  'auth.googleFailed': 'Giriş tamamlanamadı',
  'auth.googleLastStep': 'Son adım',
  'auth.reviewTerms': 'Koşulları inceleyin',
  'auth.googleContinuing': 'Google hesabıyla devam ediyorsunuz.',
  'auth.reviewBoth': 'Devam etmek için iki metni de inceleyip işaretle.',
  'auth.consentFailed': 'Onay kaydedilemedi. Tekrar dene.',
  'auth.confirmAndContinue': 'Onayla ve Wondeon Hub’a devam et',
  'auth.useDifferentAccount': 'Farklı hesapla giriş yap',
  'auth.visualAria': 'Wondeon Hub deneyim alanları',
  'auth.carouselAria': 'Deneyim görselleri',
  'auth.pause': 'Görsel geçişini durdur',
  'auth.resume': 'Görsel geçişini sürdür',
  'auth.slideLedEyebrow': 'Kamusal deneyim platformu',
  'auth.slideLedTitle': 'Ekranlarınızı yönetin. Deneyimleri başlatın.',
  'auth.slideLedBody': 'İçerik, ekran ve yayın operasyonu tek bir çalışma alanında.',
  'auth.slideVrEyebrow': 'VR deneyimleri',
  'auth.slideVrTitle': 'Oyunları lisanslayın. İstasyonları çalıştırın.',
  'auth.slideVrBody': 'VR içeriklerinizi, cihazlarınızı ve operasyon akışınızı birlikte yönetin.',
  'auth.slideImmersiveEyebrow': 'Motion Track ve Etkileşimli Dijital Sanat',
  'auth.slideImmersiveTitle': 'Hareketi sahneye taşıyın. Sanatı canlı çalıştırın.',
  'auth.slideImmersiveBody': 'Sahne performanslarını, içerikleri ve teknik çalışma yüzeylerini bir araya getirin.',
  'auth.slideArEyebrow': 'Artırılmış gerçeklik',
  'auth.slideArTitle': 'Fiziksel alanları dijital deneyimlerle genişletin.',
  'auth.slideArBody': 'AR deneyimlerinin teslim, erişim ve cihaz yaşam döngüsünü tek merkezden izleyin.',
  'nav.main': 'Ana gezinme',
  'nav.home': 'Ana sayfa',
  'nav.explore': 'Keşfet',
  'nav.myContent': 'İçeriklerim',
  'nav.devices': 'Cihazlar',
  'nav.schedule': 'Takvim ve oturumlar',
  'nav.ledAds': 'LED reklamları',
  'nav.screenStudio': 'Ekran Stüdyosu',
  'nav.reports': 'Raporlar',
  'nav.subscription': 'Abonelik',
  'nav.support': 'Destek',
  'nav.settings': 'Ayarlar',
  'nav.content': 'İçerik',
  'nav.operations': 'Operasyon',
  'nav.account': 'Hesap',
  'nav.yourCatalogs': 'Kütüphanem',
  'nav.catalogCredits': 'Krediler',
  'nav.bottom': 'Alt gezinme',
  'nav.openMenu': 'Menüyü aç',
  'nav.closeMenu': 'Menüyü kapat',
  'nav.notifications': 'Bildirimler',
  'nav.noNotifications': 'Yeni bildirim yok.',
  'nav.trialEnds': 'Denemen',
  'nav.trialAccessNotice': 'Deneme bitince erişim kapanır; otomatik ücretlendirme yapılmaz.',
  'nav.contact': 'İletişime geç →',
  'nav.contactForContinuity': 'Kesintisiz devam etmek için bizimle iletişime geç.',
  'nav.getQuote': 'Teklif Al',
  'nav.creditRemaining': 'kredinin tanesi kaldı',
  'nav.accountAndOrg': 'Hesap ve kurum ayarları',
  'admin.panel': 'Yönetici Paneli',
  'admin.panelAria': 'Yönetici paneli',
  'admin.sectionsAria': 'Yönetim bölümleri',
  'admin.openMenu': 'Yönetim menüsünü aç',
  'admin.closeMenu': 'Yönetim menüsünü kapat',
  'admin.search': 'Yönetimde ara',
  'admin.searchAria': 'Yönetim bölümlerinde ara',
  'admin.searchResults': 'Yönetim arama sonuçları',
  'admin.noSearchResult': 'Bu adla eşleşen bir yönetim bölümü yok.',
  'admin.breadcrumb': 'Sayfa yolu',
  'admin.groupPlatform': 'Platform',
  'admin.groupBusiness': 'İş',
  'admin.groupProduct': 'Ürün',
  'admin.groupManagement': 'Yönetim',
  'admin.overview': 'Kontrol Paneli',
  'admin.overviewDesc': 'Hesapların, ürünlerin, cihazların ve oturumların güncel durumu.',
  'admin.customers': 'Müşteriler',
  'admin.customersDesc': 'Kurum hesapları, kullanıcılar, erişimler ve kredi hareketleri.',
  'admin.content': 'İçerik',
  'admin.contentDesc': 'Katalog ürünleri, teslim türleri, yayın durumu ve medya yüzeyleri.',
  'admin.packages': 'Paketler',
  'admin.packagesDesc': 'Ürün paketleri, deneme kapsamı ve paket erişimleri.',
  'admin.categories': 'Kategoriler',
  'admin.categoriesDesc': 'Keşfet kategori anlatımı, modüller, kullanım modeli ve SSS.',
  'admin.banners': 'Bannerlar',
  'admin.bannersDesc': 'Hub ana sayfasındaki yayın görselleri, hedefleri ve sıralaması.',
  'admin.audit': 'Denetim',
  'admin.auditDesc': 'Yönetim işlemlerinin zaman, kullanıcı ve hedef bazlı değişmez izi.',
  'admin.accountFallback': 'Hesap',
} as const;

export type TranslationKey = keyof typeof tr;

const en: Record<TranslationKey, string> = {
  'language.switchToEnglish': 'Switch to English',
  'language.switchToTurkish': 'Switch to Turkish',
  'common.backToWondeon': 'Back to wondeon.com',
  'common.skipToContent': 'Skip to content',
  'common.searchCatalog': 'Search the catalog',
  'common.signOut': 'Sign out',
  'common.accountSettings': 'Account settings',
  'common.theme': 'Theme',
  'common.changeTheme': 'Click to change.',
  'common.themeDark': 'dark',
  'common.themeLight': 'light',
  'common.themeSystem': 'system',
  'common.legalLinks': 'Legal links',
  'common.privacy': 'Privacy Notice',
  'common.terms': 'Terms of Use',
  'common.open': 'Open',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.loading': 'Loading…',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.passwordAgain': 'Confirm password',
  'auth.newPassword': 'New password',
  'auth.newPasswordAgain': 'Confirm new password',
  'auth.fullName': 'Full name',
  'auth.organizationName': 'Organization name',
  'auth.signIn': 'Sign in',
  'auth.signInSubtitle': 'Continue to Wondeon Hub with your organization account.',
  'auth.rememberMe': 'Remember me',
  'auth.forgotPassword': 'Forgot password',
  'auth.or': 'or',
  'auth.googleSignIn': 'Continue with Google',
  'auth.noAccount': "Don't have an account?",
  'auth.register': 'Create one',
  'auth.resendVerification': 'Send a new code',
  'auth.serverUnavailable': 'Could not reach the server. Check your internet connection.',
  'auth.resendSuccess': 'A new verification code was sent. Check your inbox.',
  'auth.resendFail': 'Could not send it. Please try again shortly.',
  'auth.createOrg': 'Create an organization account',
  'auth.createOrgSubtitle': 'Start using Wondeon Hub on behalf of your organization.',
  'auth.createAccount': 'Create account',
  'auth.haveAccount': 'Already have an account?',
  'auth.passwordMin': 'At least 10 characters.',
  'auth.passwordMismatch': 'Passwords do not match.',
  'auth.completeMarked': 'Complete the marked fields to continue.',
  'auth.confirmPrivacyRead': 'Confirm that you have read the Privacy Notice.',
  'auth.confirmTerms': 'Accept the Terms of Use.',
  'auth.privacyRead': 'I have read the Privacy Notice.',
  'auth.termsAccepted': 'I accept the Terms of Use.',
  'auth.checkEmail': 'Check your email',
  'auth.verificationSent': 'was sent a six-digit verification code.',
  'auth.checkSpam': 'If it has not arrived, check your spam folder too.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.strengthTooShort': 'Too short',
  'auth.strengthWeak': 'Weak',
  'auth.strengthGood': 'Good',
  'auth.strengthStrong': 'Strong',
  'auth.resetLinkSent': 'Reset link sent',
  'auth.resetLinkSentIfRegistered': 'was sent a password reset link if the address is registered.',
  'auth.resetLinkValidity': 'The link is valid for 1 hour. Check spam if it has not arrived.',
  'auth.forgotSubtitle': 'Enter your email and we will send a reset link.',
  'auth.sendLink': 'Send reset link',
  'auth.invalidLink': 'Invalid link',
  'auth.invalidResetLink': 'This link has no reset code. Open the link from your email again or request a new one.',
  'auth.requestNewLink': 'Request a new link',
  'auth.setNewPassword': 'Set a new password',
  'auth.resetClosesSessions': 'Saving will sign you out of all existing sessions.',
  'auth.updatePassword': 'Update password',
  'auth.passwordUpdated': 'Your password was updated. You can now sign in with it.',
  'auth.verifyingEmail': 'Verifying email',
  'auth.verifying': 'Verifying',
  'auth.emailVerified': 'Email verified',
  'auth.canSignIn': 'You can now sign in.',
  'auth.verificationFailed': 'Verification failed',
  'auth.noVerificationCode': 'This link has no verification code.',
  'auth.verificationRetry': 'The link may have expired. Request a new verification link from the sign-in page.',
  'auth.googleVerifying': 'Verifying Google account',
  'auth.googlePendingMissing': 'No pending Google sign-in was found.',
  'auth.googleInfoFailed': 'Could not retrieve Google sign-in information.',
  'auth.googleFailed': 'Sign-in could not be completed',
  'auth.googleLastStep': 'Final step',
  'auth.reviewTerms': 'Review the terms',
  'auth.googleContinuing': 'You are continuing with this Google account.',
  'auth.reviewBoth': 'Review and select both documents to continue.',
  'auth.consentFailed': 'Consent could not be saved. Try again.',
  'auth.confirmAndContinue': 'Accept and continue to Wondeon Hub',
  'auth.useDifferentAccount': 'Use a different account',
  'auth.visualAria': 'Wondeon Hub experience areas',
  'auth.carouselAria': 'Experience visuals',
  'auth.pause': 'Pause visual transitions',
  'auth.resume': 'Resume visual transitions',
  'auth.slideLedEyebrow': 'Public experience platform',
  'auth.slideLedTitle': 'Manage your displays. Launch experiences.',
  'auth.slideLedBody': 'Content, displays and broadcast operations in one workspace.',
  'auth.slideVrEyebrow': 'VR experiences',
  'auth.slideVrTitle': 'License games. Run your stations.',
  'auth.slideVrBody': 'Manage VR content, devices and operations together.',
  'auth.slideImmersiveEyebrow': 'Motion Track and Immersive Digital Art',
  'auth.slideImmersiveTitle': 'Bring movement to the stage. Run art live.',
  'auth.slideImmersiveBody': 'Bring stage performances, content and technical workspaces together.',
  'auth.slideArEyebrow': 'Augmented reality',
  'auth.slideArTitle': 'Extend physical spaces with digital experiences.',
  'auth.slideArBody': 'Track delivery, access and device lifecycles for AR experiences from one place.',
  'nav.main': 'Main navigation',
  'nav.home': 'Home',
  'nav.explore': 'Discover',
  'nav.myContent': 'My content',
  'nav.devices': 'Devices',
  'nav.schedule': 'Calendar and sessions',
  'nav.ledAds': 'LED advertising',
  'nav.screenStudio': 'Screen Studio',
  'nav.reports': 'Reports',
  'nav.subscription': 'Subscription',
  'nav.support': 'Support',
  'nav.settings': 'Settings',
  'nav.content': 'Content',
  'nav.operations': 'Operations',
  'nav.account': 'Account',
  'nav.yourCatalogs': 'My library',
  'nav.catalogCredits': 'Credits',
  'nav.bottom': 'Bottom navigation',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.notifications': 'Notifications',
  'nav.noNotifications': 'No new notifications.',
  'nav.trialEnds': 'Your trial',
  'nav.trialAccessNotice': 'Access ends with the trial; there is no automatic billing.',
  'nav.contact': 'Contact us →',
  'nav.contactForContinuity': 'Contact us to continue without interruption.',
  'nav.getQuote': 'Request a Quote',
  'nav.creditRemaining': 'credits remaining out of',
  'nav.accountAndOrg': 'Account and organization settings',
  'admin.panel': 'Admin Panel',
  'admin.panelAria': 'Admin panel',
  'admin.sectionsAria': 'Admin sections',
  'admin.openMenu': 'Open admin menu',
  'admin.closeMenu': 'Close admin menu',
  'admin.search': 'Search admin',
  'admin.searchAria': 'Search admin sections',
  'admin.searchResults': 'Admin search results',
  'admin.noSearchResult': 'No admin section matches this search.',
  'admin.breadcrumb': 'Breadcrumb',
  'admin.groupPlatform': 'Platform',
  'admin.groupBusiness': 'Business',
  'admin.groupProduct': 'Product',
  'admin.groupManagement': 'Management',
  'admin.overview': 'Control Panel',
  'admin.overviewDesc': 'Current state of accounts, products, devices and sessions.',
  'admin.customers': 'Customers',
  'admin.customersDesc': 'Organizations, users, access rights and credit activity.',
  'admin.content': 'Content',
  'admin.contentDesc': 'Catalog products, delivery types, publishing state and media.',
  'admin.packages': 'Packages',
  'admin.packagesDesc': 'Product packages, trial scope and package access.',
  'admin.categories': 'Categories',
  'admin.categoriesDesc': 'Discover narratives, modules, usage model and FAQs.',
  'admin.banners': 'Banners',
  'admin.bannersDesc': 'Homepage visuals, destinations and display order.',
  'admin.audit': 'Audit Log',
  'admin.auditDesc': 'Immutable history of admin actions by time, user and target.',
  'admin.accountFallback': 'Account',
};

interface LanguageContextValue {
  language: LanguagePreference;
  locale: 'tr-TR' | 'en-US';
  setLanguage: (value: LanguagePreference) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLanguage(): LanguagePreference {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'tr';
  } catch {
    return 'tr';
  }
}

function persistLanguage(value: LanguagePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* Gizli sekmede depolama kapali olabilir. */
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { me, setLanguagePreference } = useAuth();
  const queryClient = useQueryClient();
  const [language, setLanguageState] = useState<LanguagePreference>(() => {
    const initialLanguage = readStoredLanguage();
    // İlk React boyaması da seçili dile ait tarih/sayı biçimlerini kullanmalı.
    // Etkiye bırakılırsa metin İngilizceyken ilk karede Türkçe ay adı kalır.
    setFormatLanguage(initialLanguage);
    setRequestLanguage(initialLanguage);
    return initialLanguage;
  });

  useEffect(() => {
    if (!me) return;
    setFormatLanguage(me.user.language);
    setRequestLanguage(me.user.language);
    setLanguageState(me.user.language);
    persistLanguage(me.user.language);
  }, [me?.user.language, me?.user.id]);

  useEffect(() => {
    document.documentElement.lang = language;
    setFormatLanguage(language);
    setRequestLanguage(language);
  }, [language]);

  const setLanguage = useCallback((value: LanguagePreference) => {
    // State güncellemesi aynı render turunu başlatacağı için paylaşılan
    // biçimleyicileri önceden hizalarız; aksi halde tarihler bir dil geriden gelir.
    setFormatLanguage(value);
    setRequestLanguage(value);
    setLanguageState(value);
    persistLanguage(value);
    // Sunucuda yerelleştirilen katalog, banner ve satış anlatımları eski dilde
    // önbellekte kalmasın. Anahtarları tek tek saymak yeni bir ekran eklenince
    // sessizce eksik kalır; dil değişimi seyrek olduğu için tüm aktif sorguları
    // tazelemek daha güvenilir.
    void queryClient.invalidateQueries();
    if (!me) return;
    setLanguagePreference(value);
    // Dil secimi ag hatasinda da bu cihazda calisir; sonraki basarili secimde
    // sunucu yeniden hizalanir. Arayuzu yazma istegine kilitlemeyiz.
    void api.patch('/account/preferences', { language: value }).catch(() => {});
  }, [me, queryClient, setLanguagePreference]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    locale: language === 'tr' ? 'tr-TR' : 'en-US',
    setLanguage,
    t: (key) => (language === 'tr' ? tr[key] : en[key]),
  }), [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useI18n yalnızca LanguageProvider içinde kullanılabilir.');
  return context;
}

/** Küçük, route'a özgü metinlerde sözlüğü gereksiz yere büyütmeden iki dili açık tutar. */
export function copy(language: LanguagePreference, turkish: string, english: string): string {
  return language === 'tr' ? turkish : english;
}

/** API'deki kanonik katalog adi route kimligi olarak kalir; yalniz gorunen etiket cevrilir. */
export function localizeCatalogName(name: string, language: LanguagePreference): string {
  const canonicalName = canonicalCatalogName(name);
  if (language === 'tr') {
    return canonicalName === 'Immersive Digital Art' ? 'Etkileşimli Dijital Sanat' : canonicalName;
  }
  const names: Record<string, string> = {
    'Leds Play': 'Leds Play',
    'VR Deneyimleri': 'VR Experiences',
    'Immersive Digital Art': 'Immersive Digital Art',
    'AR Deneyimleri': 'AR Experiences',
    'Servisler': 'Services',
  };
  return names[canonicalName] ?? canonicalName;
}
